import {
  classNames,
  FlexCenter,
  FlexColumn,
  IconCallout,
  SpaceBetween,
  TextBody,
  TextHeading,
  Tooltip,
} from "@opensea/ui-kit"
import { type IconComponent, Info } from "@opensea/ui-kit/icons"
import { useTranslations } from "next-intl"

export type StudioPageHeaderProps = {
  title: string | React.ReactNode
  description?: string | React.ReactNode
  control?: React.ReactNode
  icon?: IconComponent
  iconLayout?: "default" | "inline"
  tooltip?: string | React.ReactNode
  tooltipClassName?: string
  optional?: boolean
  size?: "lg" | "sm" | "xs"
  className?: string
}

export function StudioPageHeader({
  title,
  description,
  control,
  icon: IconComponent,
  iconLayout = "default",
  tooltip,
  tooltipClassName = "w-[320px] text-center",
  optional,
  size = "lg",
  className,
}: StudioPageHeaderProps) {
  const t = useTranslations("StudioPageHeader")

  const icon = IconComponent && (
    <IconCallout icon={IconComponent} size={20} variant="outlined" />
  )

  return (
    <FlexColumn className={classNames("gap-5", className)}>
      {iconLayout === "default" && icon}
      <FlexColumn className="gap-2">
        <SpaceBetween className="items-center">
          <FlexCenter
            className={classNames(size !== "xs" ? "gap-2" : "gap-1.5")}
          >
            {/* Inline Icon */}
            {iconLayout === "inline" && icon}
            {/* Title sizes */}
            {size === "lg" && <TextHeading size="md">{title}</TextHeading>}
            {(size === "sm" || size === "xs") && (
              <TextBody size="sm" weight="semibold">
                {title}
              </TextBody>
            )}
            {/* (Optional) */}
            {optional ? (
              <TextBody color="text-secondary" size="sm">
                {t("optional")}
              </TextBody>
            ) : null}
            {/* Tooltip */}
            {tooltip ? (
              <Tooltip
                className={tooltipClassName}
                content={<TextBody size="sm">{tooltip}</TextBody>}
              >
                <Info fill="text-secondary" size={16} />
              </Tooltip>
            ) : null}
          </FlexCenter>
          {control}
        </SpaceBetween>
        {description && (
          <TextBody color="text-secondary" size={size === "xs" ? "xs" : "sm"}>
            {description}
          </TextBody>
        )}
      </FlexColumn>
    </FlexColumn>
  )
}
